import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { hasPermission } from '../utils/permissions';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [myLocation, setMyLocation] = useState(null);

  // Fetch current locations for all users
  const fetchCurrentLocations = useCallback(async () => {
    try {
      // Gate on auth + permission at call time so we don't 403 for users who
      // don't have location_tracking view permission.
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (!storedUser?.id || !hasPermission(storedUser, 'location_tracking', 'view')) {
        setLoading(false);
        return;
      }
      const response = await api.get('/locations/current');
      setLocations(response.data);
      setLoading(false);
    } catch (error) {
      // Silent: 403 for restricted roles is expected; no need to spam console
      setLoading(false);
    }
  }, []);

  // Update my location
  const updateMyLocation = useCallback(async (latitude, longitude) => {
    try {
      await api.post('/locations', {
        latitude,
        longitude
      });
      setMyLocation({ latitude, longitude, timestamp: new Date().toISOString() });
      return { success: true };
    } catch (error) {
      console.error('Error updating location:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Start tracking my location
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        updateMyLocation(latitude, longitude);
      },
      (error) => {
        // Permission denied / position unavailable / timeout — degrade silently
        console.warn('Geolocation unavailable:', error?.message || error?.code);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [updateMyLocation]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    // Gate everything on having a logged-in user. The cookie-only auth flow
    // means no token in localStorage, so we use the cached user object as the
    // auth-state signal. Users without location_tracking view skip entirely.
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (!storedUser?.id || !hasPermission(storedUser, 'location_tracking', 'view')) {
      setLoading(false);
      return;
    }

    fetchCurrentLocations();

    // Get backend URL from helper (auto-detects current origin in production)
    const backendUrl = (typeof window !== 'undefined' ? window.location.origin : null) || 'http://localhost:8001';
    // K8s ingress only routes /api/* to backend, so WS endpoint must live under /api/
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws/locations';

    let websocket;
    try {
      websocket = new WebSocket(wsUrl);
    } catch (_err) {
      // If WebSocket construction fails (older browsers, blocked ports),
      // fall back to HTTP polling without polluting the console.
      setLoading(false);
      const poll = setInterval(fetchCurrentLocations, 30000);
      return () => clearInterval(poll);
    }

    websocket.onopen = () => {
      // Connected — real-time updates will arrive via onmessage.
    };

    websocket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (_err) {
        return;
      }
      
      if (data.type === 'location_update') {
        // Update the location for the specific user
        setLocations((prev) => {
          const userIndex = prev.findIndex((loc) => loc.user?.id === data.user_id);
          
          if (userIndex !== -1) {
            // Update existing user location
            const updated = [...prev];
            updated[userIndex] = {
              ...updated[userIndex],
              location: {
                latitude: data.latitude,
                longitude: data.longitude,
                timestamp: data.timestamp
              }
            };
            return updated;
          }
          
          // If user not found, fetch all locations again
          fetchCurrentLocations();
          return prev;
        });
      }
    };

    // Silent failure — proxy/ingress may not support WS upgrade in some
    // environments. We already loaded an initial snapshot via
    // fetchCurrentLocations(), so the page still works without realtime.
    websocket.onerror = () => {};
    websocket.onclose = () => {};

    setWs(websocket);

    return () => {
      if (websocket && websocket.readyState !== WebSocket.CLOSED) {
        try { websocket.close(); } catch (_err) { /* noop */ }
      }
    };
  }, [fetchCurrentLocations]);

  const value = {
    locations,
    loading,
    myLocation,
    updateMyLocation,
    startTracking,
    refetchLocations: fetchCurrentLocations
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
